import React, { useCallback, useMemo } from 'react';

import { Box } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { token } from '@betterment/samba-styled-system/tokens';
import { AxisBottom } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { Bar, Line, LinePath } from '@visx/shape';

import { formatDate, formatTimeDisplay } from './dateTime';
import { type PricePointType } from './pricePoints';
import { isDateRangeOptionToday } from './timePeriods';

import { DateRangeOption } from '../../../../../graphql/schema';
import { useXYChartTooltipInPortal } from '../../../../../Shared/components-v2/charts/components/XYChartTooltip';
import {
  IndexDomainScale,
  useChartHighlight,
} from '../../../../../Shared/components-v2/charts/hooks/useChartHighlight';
import { useTickLabelProps } from '../../../../../Shared/components-v2/charts/hooks/useTickLabelProps';

type PerformanceChartProps = {
  dateRangeOption: DateRangeOption;
  data: PricePointType[];
  height: number;
  width: number;
  disabled?: boolean;
  onHighlightChange: (highlight: PricePointType | null) => void;
};

const defaultMargin = { top: 10, right: 1, bottom: 20, left: 1 };

export const PerformanceChart: React.FC<PerformanceChartProps> = ({
  dateRangeOption,
  data: remoteData,
  width,
  height,
  disabled = false,
  onHighlightChange,
}) => {
  const data = useMemo(() => remoteData.map(d => ({ ...d, dateTime: new Date(d.dateTime) })), [remoteData]);
  const xMax = Math.max(0, width - defaultMargin.left - defaultMargin.right);
  const yMax = Math.max(0, height - defaultMargin.top - defaultMargin.bottom);

  const pricePointsWithAmount = data.filter(d => d.amount !== null && d.amount.cents != null);
  const latestPricePointWithAmount = pricePointsWithAmount[pricePointsWithAmount.length - 1];
  const latestPricePointWithAmountIndex = data.indexOf(latestPricePointWithAmount);
  const hasLatestPricePointWithAmount = latestPricePointWithAmountIndex >= 0;

  const performanceScale = useMemo(() => {
    const prices = data.filter(e => e.amount !== null).map(d => d.amount!.cents);

    if (prices.length === 0) {
      return scaleLinear({ range: [yMax, 0], domain: [0, 1], nice: true });
    }

    const [min, max] = [Math.min(...prices), Math.max(...prices)];
    const average = (max + min) / 2;
    const offset = ((max - min) / 2) * 1.03;
    return scaleLinear({ range: [yMax, 0], domain: [average - offset, average + offset], nice: true });
  }, [yMax, data]);

  const isDateRangeOptionTodayValue = isDateRangeOptionToday(dateRangeOption);

  const xTickFormat = useCallback(
    (v: { valueOf(): number }, i: number) => {
      const index = v.valueOf();
      if (data[index] === undefined) {
        return '';
      }

      if (isDateRangeOptionTodayValue) {
        return formatTimeDisplay(data[index].dateTime);
      }
      if (i === 1) {
        return 'Today';
      }
      return formatDate(data[index].dateTime);
    },
    [data, isDateRangeOptionTodayValue]
  );

  const dateScale = useMemo(() => scaleLinear({ range: [0, xMax], domain: [0, data.length - 1] }), [xMax, data.length]);

  const onHighlightChangeWithAmounts = useCallback(
    (highlight: PricePointType | null) => {
      if (highlight != null && highlight.amount != null && highlight.amount.cents != null) {
        onHighlightChange(highlight);
      } else {
        onHighlightChange(null);
      }
    },
    [onHighlightChange]
  );

  const chartHighlight = useChartHighlight<PricePointType, IndexDomainScale>({
    dateScale,
    data,
    xAccessor: d => data.indexOf(d),
    disabled,
    marginLeft: defaultMargin.left,
    onHighlightChange: onHighlightChangeWithAmounts,
  });
  const { highlightData, handleHighlightUpdate, handleHighlightCancel } = chartHighlight;

  const { XYChartTooltipInPortal, containerRef } = useXYChartTooltipInPortal();

  const hasHighlightAmount = highlightData?.highlight?.amount?.cents != null;
  const tickLabelProps = useTickLabelProps({ highlightData: hasHighlightAmount ? highlightData : null });

  const highlightIndex = useMemo(() => {
    if (!hasHighlightAmount) return -1;
    return data.findIndex(d => d.dateTime.getTime() === highlightData.highlight.dateTime.getTime());
  }, [hasHighlightAmount, data, highlightData?.highlight?.dateTime]);

  const shouldShowHighlight = useMemo(() => {
    return hasHighlightAmount && highlightIndex >= 0;
  }, [hasHighlightAmount, highlightIndex]);

  const xYChartTooltipInPortalValue = useCallback(
    (dateTime: Date) => {
      if (isDateRangeOptionTodayValue) {
        return formatTimeDisplay(dateTime);
      }
      return formatDate(dateTime);
    },
    [isDateRangeOptionTodayValue]
  );

  return (
    <Box css={{ position: 'relative', width: '100%', maxWidth: width }}>
      <svg
        width={width}
        height={height}
        ref={containerRef}
        aria-label="Price performance chart"
        style={{ maxWidth: '100%' }}
      >
        <rect x={0} y={0} width={width} height={height} fill={token('colors.background.surface')} />
        <Group left={defaultMargin.left} top={defaultMargin.top}>
          <GridRows
            scale={performanceScale}
            width={xMax}
            height={yMax}
            strokeDasharray="4,4"
            stroke={token('colors.border.weak')}
            pointerEvents="none"
            numTicks={5}
          />
          <AxisBottom
            scale={dateScale}
            top={yMax}
            tickValues={[0, data.length - 1]}
            tickFormat={xTickFormat}
            hideTicks={true}
            rangePadding={0}
            labelOffset={0}
            axisLineClassName={css({ stroke: 'border' })}
            tickLabelProps={tickLabelProps}
          />
          {/*  Amount line */}
          <LinePath
            className={css({ stroke: 'dataviz.blue.2', strokeWidth: '2px' })}
            data={data}
            strokeLinejoin="round"
            x={(_, i) => dateScale(i)}
            y={d => performanceScale(d.amount?.cents ?? 0)}
            defined={d => d.amount !== null && d.amount.cents != null}
          />
          {hasLatestPricePointWithAmount && !shouldShowHighlight && (
            <circle
              cx={dateScale(latestPricePointWithAmountIndex)}
              cy={performanceScale(latestPricePointWithAmount.amount!.cents)}
              r={token('sizes.size.50')}
              fill={token('colors.dataviz.blue.2')}
              stroke={token('colors.border.onPrimary')}
              strokeWidth={token('sizes.size.1')}
              pointerEvents="none"
            />
          )}
          <Bar
            x={0}
            y={0}
            width={xMax}
            height={yMax}
            fill="transparent"
            onTouchStart={handleHighlightUpdate}
            onTouchMove={handleHighlightUpdate}
            onMouseMove={handleHighlightUpdate}
            onMouseLeave={handleHighlightCancel}
          />
          {shouldShowHighlight &&
            (() => {
              const highlightX = dateScale(highlightIndex);

              return (
                <g>
                  <Line
                    from={{ x: highlightX, y: -10 }}
                    to={{ x: highlightX, y: height - defaultMargin.bottom - defaultMargin.top }}
                    stroke={token('colors.dataviz.blue.2')}
                    strokeWidth={token('sizes.size.1')}
                    pointerEvents="none"
                  />
                  <circle
                    cx={highlightX}
                    cy={performanceScale(highlightData!.highlight.amount!.cents)}
                    r={token('sizes.size.50')}
                    fill={token('colors.dataviz.blue.2')}
                    stroke={token('colors.border.onPrimary')}
                    strokeWidth={token('sizes.size.1')}
                    pointerEvents="none"
                  />
                </g>
              );
            })()}
        </Group>
      </svg>
      {shouldShowHighlight && (
        <Box>
          <XYChartTooltipInPortal tooltipLeft={dateScale(highlightIndex)} chartHeight={height} margin={defaultMargin}>
            {xYChartTooltipInPortalValue(highlightData!.highlight.dateTime)}
          </XYChartTooltipInPortal>
        </Box>
      )}
    </Box>
  );
};
